import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
// @mui
import { styled } from '@mui/material/styles';
import { Slide, Autocomplete, TextField, InputAdornment, ClickAwayListener } from '@mui/material';
// utils
import cssStyles from '../../../utils/cssStyles';
// components
import Iconify from '../../../components/Iconify';
import { IconButtonAnimate } from '../../../components/animate';
// hooks
import useLocales from '../../../hooks/useLocales';
// redux
import { useDispatch } from '../../../redux/store';
import { getSearch } from '../../../redux/slices/search';

// ----------------------------------------------------------------------

const APPBAR_MOBILE = 64;
const APPBAR_DESKTOP = 92;

const SearchbarStyle = styled('div')(({ theme }) => ({
  ...cssStyles(theme).bgBlur(),
  top: 0,
  left: 0,
  zIndex: 99,
  width: '100%',
  display: 'flex',
  position: 'absolute',
  alignItems: 'center',
  height: APPBAR_MOBILE,
  padding: theme.spacing(0, 3),
  boxShadow: theme.customShadows.z8,
  [theme.breakpoints.up('md')]: {
    height: APPBAR_DESKTOP,
    padding: theme.spacing(0, 5),
  },
}));

// ----------------------------------------------------------------------

export default function Searchbar() {
  const [isOpen, setOpen] = useState(false);
  const { translate } = useLocales();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [data, setData] = useState([]);

  const handleOpen = () => {
    setOpen((prev) => !prev);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSearch = async (event, newInputValue) => {
    if (newInputValue) {
      try {
        const request = await dispatch(getSearch(newInputValue));
        setData(request?.data?.results || []);
      } catch (error) {
        console.error(error);
        setData([]);
      }
    }
  };

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <div>
        {!isOpen && (
          <IconButtonAnimate id="search" onClick={handleOpen}>
            <Iconify icon={'eva:search-fill'} width={20} height={20} />
          </IconButtonAnimate>
        )}

        <Slide direction="down" in={isOpen} mountOnEnter unmountOnExit>
          <SearchbarStyle>
            <Autocomplete
              selectOnFocus
              clearOnBlur
              onChange={(event, newValue) => {
                if (newValue?.link) navigate(newValue.link);
                setOpen(false);
              }}
              options={data}
              groupBy={(option) => translate(`layout.navbar.search.group.${option.group}`)}
              getOptionLabel={(option) => option.label || ''}
              sx={{ width: '100%' }}
              renderInput={(params) => (
                <TextField
                  autoFocus
                  fullWidth
                  placeholder={translate('layout.navbar.search.root')}
                  {...params}
                  InputProps={{
                    ...params.InputProps,
                    disableUnderline: true,
                    startAdornment: (
                      <InputAdornment position="start">
                        <Iconify icon={'eva:search-fill'} width={20} height={20} />
                      </InputAdornment>
                    ),
                  }}
                  variant="standard"
                />
              )}
              onInputChange={handleSearch}
            />
          </SearchbarStyle>
        </Slide>
      </div>
    </ClickAwayListener>
  );
}
